import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";
import { MdSos, MdOutlineEmail, MdLockOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const role = await login(email, password);

      if (role === "admin") {
        navigate("/admin");
      } else if (role === "Cobrador") {
        navigate("/cobrador");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError("Credenciales inválidas. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex flex-col justify-center items-center min-h-screen px-5 overflow-hidden"
      style={{ background: "linear-gradient(160deg, #2d0a14 0%, #5c031e 35%, #701730 60%, #8a1e3d 100%)" }}
    >
      {/* Círculos decorativos de fondo */}
      <div className="absolute top-[-80px] left-[-80px] w-64 h-64 rounded-full opacity-10 bg-white" />
      <div className="absolute bottom-[-60px] right-[-60px] w-52 h-52 rounded-full opacity-[0.07] bg-white" />
      <div className="absolute top-1/3 right-[-30px] w-24 h-24 rounded-full opacity-[0.05] bg-white" />

      {/* Logo */}
      <div className="mb-8 flex flex-col items-center">
        <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/10">
          <img src={logo} alt="SAC" className="w-16 h-16 object-contain" />
        </div>
        <h1 className="mt-4 text-white text-2xl font-bold tracking-tight">SAC</h1>
        <p className="text-white/50 text-sm mt-1">Sistema de Administración de Cobros</p>
      </div>

      {/* Card del formulario */}
      <div className="w-full max-w-sm">
        <div
          className="rounded-3xl p-6 shadow-2xl border border-white/10"
          style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}
        >
          <h2 className="text-xl font-bold text-white text-center mb-1">Bienvenido</h2>
          <p className="text-white/50 text-sm text-center mb-6">Inicia sesión para continuar</p>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-2xl bg-red-500/15 border border-red-400/20">
              <p className="text-red-300 text-sm text-center font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-white/60 mb-1.5 ml-1">
                Correo Electrónico
              </label>
              <div className="relative">
                <MdOutlineEmail size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-white/10 border border-white/10 text-white placeholder-white/25 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-white/30 focus:bg-white/15 transition-all"
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-white/60 mb-1.5 ml-1">
                Contraseña
              </label>
              <div className="relative">
                <MdLockOutline size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-white/10 border border-white/10 text-white placeholder-white/25 rounded-xl pl-11 pr-11 py-3 text-sm focus:outline-none focus:border-white/30 focus:bg-white/15 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 py-3.5 rounded-xl font-bold text-sm text-white transition-all active:scale-[0.98] disabled:opacity-60 disabled:active:scale-100"
              style={{ background: "linear-gradient(135deg, #a3243e, #701730)" }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Ingresando…
                </span>
              ) : (
                "Iniciar Sesión"
              )}
            </button>
          </form>
        </div>

        {/* Footer sutil */}
        <p className="text-center text-white/20 text-xs mt-6">
          © {new Date().getFullYear()} SAC · Todos los derechos reservados
        </p>
      </div>

      {/* Botón SOS */}
      <button
        onClick={() => window.open("https://auxsac.igob.mx/", "_blank", "noopener,noreferrer")}
        title="SOS"
        aria-label="SOS"
        className="fixed bottom-5 right-5 z-[110] h-14 w-14 rounded-full shadow-lg bg-red-600 text-white flex items-center justify-center hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-white/60 active:scale-95 transition-transform"
      >
        <MdSos size={28} />
      </button>
    </div>
  );
};

export default LoginPage;
